import { AlertTriangle, Sparkles } from "lucide-react";
import { cn } from "@wa/ui";
import { useT } from "../../../i18n/I18nProvider";
import { useFlexSplit } from "../../ui";
import type { LivePilot } from "./useLivePilot";
import { SuggestionList } from "./SuggestionList";
import { TranscriptList } from "./TranscriptList";

/**
 * The operator's own boundary between the two halves, per browser and per axis.
 *
 * Two keys behind the one prefix, because stacked and side by side are not the
 * same choice: 75% of the height given to the transcript is a reasonable thing
 * to want on a card and a nearly empty screen once the window is filling one.
 */
const SPLIT_KEY = "crm.call.pilot.split";

/** Neither half is useful below this, so neither may be dragged under it. */
const MIN_PANE = 110;

/**
 * The co-pilot panel: the conversation as it happens, and what to ask next.
 *
 * It does not animate itself. The whole panel is inside the call window's morph
 * (`CallWindow.tsx`, one `layoutId`), and a second animation on the inside would
 * be a second opinion about where things are going - which is exactly the
 * `AnimatePresence` inside a portal trap this app has hit before. The window
 * widens, this appears in the space that opened; that is the entire gesture.
 *
 * A minimum height is set here rather than left to the content: a panel that
 * grows line by line as the first transcript arrives would resize the call
 * window under the operator's cursor while they are reaching for mute.
 */
export function PilotPanel({ pilot, onAddToNote }: { pilot: LivePilot; onAddToNote: (text: string) => void }) {
  const t = useT();
  const { status } = pilot;
  // The divider between the transcript and the advice. The hook reads which way
  // round the two are from the box itself, so the same handle drags downwards on
  // the card and sideways once the window is filling the screen.
  const split = useFlexSplit({ minPane: MIN_PANE, storageKey: SPLIT_KEY });

  const tone =
    status === "listening"
      ? "ok"
      // A held call is a normal pause, not a problem: the warn dot would say
      // something is wrong when the operator is the one who paused it.
      : status === "waiting" || status === "loading" || status === "on_hold"
        ? "idle"
        : "warn";

  // A lookup rather than the eight-deep ternary this was: every state here maps
  // to exactly one key, the nesting carried no meaning, and the day a ninth
  // state arrived it was misindented into the wrong branch before anybody read
  // it. `failed` is the fallback because it is the only one that is a fault.
  const STATUS_KEY: Partial<Record<typeof status, string>> = {
    listening: "calls.pilot.listening",
    waiting: "calls.pilot.waiting",
    loading: "calls.pilot.loading",
    off: "calls.pilot.off",
    budget_spent: "calls.pilot.budgetSpent",
    quota_spent: "calls.pilot.quotaSpent",
    no_audio: "calls.pilot.noAudio",
    on_hold: "calls.pilot.onHold",
  };
  const statusLabel = t(STATUS_KEY[status] ?? "calls.pilot.failed");

  return (
    <aside className="crm-cp flex min-h-0 flex-col" aria-label={t("calls.pilot.title")}>
      <header className="crm-cp-top">
        <Sparkles className="size-3.5 opacity-70" />
        <span className="text-[12.5px] font-[680] tracking-[-0.02em]">{t("calls.pilot.title")}</span>
        <span className={cn("crm-cp-dot", `is-${tone}`)} />
        <span className="truncate text-[11.5px] text-muted-foreground">{statusLabel}</span>
      </header>

      {/* The co-pilot is listening and this call announces nothing, because
          somebody switched the exemption on in Settings. Said on every call
          rather than once at setup: a waiver that goes quiet is a waiver
          nobody remembers granting. */}
      {status !== "off" && pilot.blocker === "no_notice_waived" ? (
        <p className="crm-cp-warn">
          <AlertTriangle className="mt-[2px] size-3.5 shrink-0" />
          <span>{t("calls.pilot.noticeWaivedHint")}</span>
        </p>
      ) : null}

      {/* One line, not a dialog: the operator is mid-sentence with a customer,
          and anything that needs dismissing is worse than the problem. */}
      {status === "off" ||
      status === "no_audio" ||
      status === "failed" ||
      status === "budget_spent" ||
      status === "quota_spent" ? (
        <p className="crm-cp-warn">
          <AlertTriangle className="mt-[2px] size-3.5 shrink-0" />
          <span>
            {status === "off"
              ? t(
                  // Not a fault and must not read as one: everything is
                  // configured and the person on the phone asked us to stop.
                  // Tested first, or a call on an unconfigured account would be
                  // told to go and fix Settings mid-objection.
                  pilot.blocker === "recording_stopped"
                    ? "calls.pilot.recordingStoppedHint"
                    : pilot.blocker === "no_notice"
                    ? "calls.pilot.noticeHint"
                    : // Settings are correct and this ONE call went out without
                      // an announcement, so pointing at the settings page would
                      // send the operator somewhere there is nothing to fix.
                      pilot.blocker === "no_call_notice"
                      ? "calls.pilot.callNoticeHint"
                      : pilot.blocker === "no_key"
                        ? "calls.pilot.keyHint"
                        : "calls.pilot.offHint",
                )
              : status === "budget_spent"
                ? t("calls.pilot.budgetHint", { windows: pilot.maxWindows })
                : // The account, not this call. `budget_spent` means a long
                  // conversation and nothing is wrong; this one means the day
                  // is out or the reserve has closed the co-pilot early so the
                  // transcripts of calls already made can still be written.
                  status === "quota_spent"
                  ? t("calls.pilot.quotaHint")
                : t("calls.pilot.errorHint")}
            {pilot.reason ? <span className="crm-cp-code"> {pilot.reason}</span> : null}
          </span>
        </p>
      ) : null}

      {/* The far end was never captured: the operator's own half is still worth
          reading, but they must not be left thinking the caller said nothing. */}
      {pilot.reason === "no_remote_stream" && status !== "no_audio" ? (
        <p className="crm-cp-warn">
          <AlertTriangle className="mt-[2px] size-3.5 shrink-0" />
          <span>{t("calls.pilot.noRemote")}</span>
        </p>
      ) : null}

      <div ref={split.boxRef} className="crm-cp-body flex min-h-0 flex-1">
        <TranscriptList turns={pilot.turns} pending={pilot.pending} />
        <SuggestionList
          state={pilot.state}
          checklist={pilot.checklist}
          facts={pilot.facts}
          hadContact={pilot.hadContact}
          scanUncertain={pilot.scanUncertain}
          onAddToNote={onAddToNote}
        />
        {/* Last in the box and absolutely positioned, so it sits ON the hairline
            between the two panes without taking a strip of the split for itself.
            A divider that occupied real space would make the two bases add up to
            more than the box and both panes would shrink to pay for it. */}
        <button title={t("calls.pilot.resize")} aria-label={t("calls.pilot.resize")} {...split.handleProps}>
          <i className="crm-cp-splitline" />
        </button>
      </div>
    </aside>
  );
}
